import { Controller } from "@hotwired/stimulus";
import { Turbo } from "@hotwired/turbo-rails";

// Keeps a long-lived page current by re-requesting it on a timer.
//
// A "replace" visit to the page's own url is what Turbo calls a page refresh,
// so this morphs the new markup into the live DOM rather than swapping the
// document: scroll position and the query string both survive, and the server
// renders the page for whoever is looking at it, with their own filters and
// through the usual authorization. That also means everything on the page keeps
// up, not just the parts someone remembered to broadcast for.
export default class extends Controller {
  static values = { interval: { type: Number, default: 30000 } };

  connect() {
    this.start();
    document.addEventListener("visibilitychange", this.onVisibilityChange);
    document.addEventListener(
      "turbo:before-fetch-request",
      this.tagRefreshRequest,
    );
  }

  disconnect() {
    this.stop();
    document.removeEventListener("visibilitychange", this.onVisibilityChange);
    document.removeEventListener(
      "turbo:before-fetch-request",
      this.tagRefreshRequest,
    );
  }

  start() {
    this.stop();
    this.timer = setInterval(() => this.refresh(), this.intervalValue);
  }

  stop() {
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
  }

  // A hidden tab is nobody's dashboard, so stop asking for it and catch up the
  // moment it comes back rather than waiting out the rest of the interval.
  onVisibilityChange = () => {
    if (document.visibilityState === "visible") {
      this.refresh();
      this.start();
    } else {
      this.stop();
    }
  };

  // Marks the request this refresh is about to make, so the server can tell a
  // timer re-requesting the page from an educator actually arriving at it and
  // leave the Page View event to the latter. See PageViewTracking.
  //
  // Turbo.visit gives us nowhere to pass a header, so we flag the next request
  // instead. Nothing can slip in between: the fetch event is dispatched in the
  // same synchronous flow as the visit, before the browser can process another
  // click.
  tagRefreshRequest = (event) => {
    if (!this.refreshPending) return;

    this.refreshPending = false;
    event.detail.fetchOptions.headers["X-Auto-Refresh"] = "1";
  };

  refresh() {
    if (document.visibilityState !== "visible") return;
    // Refreshing under an open slideover or modal would pull what the educator
    // is reading out from under them.
    if (document.querySelector("dialog[open]")) return;

    this.refreshPending = true;
    Turbo.visit(window.location.href, { action: "replace" });
  }
}
